import { useSnackbar } from 'providers/snackbar';
import { useCallback, useEffect, useState } from 'react';

const LOCAL_STORAGE_PREFIX = 'aktify_feature_';

type SnackbarFn = (message: string, enabled: boolean) => void;

/**
 * Shared per-flag state so multiple components using the same flag stay in sync
 * and a shortcut toggles exactly once (one localStorage write, one snackbar),
 * no matter how many instances are mounted. Without this, each instance kept its
 * own state + keydown listener, so a shortcut fired N toasts and could desync.
 */
interface FlagEntry {
  value: boolean;
  /** React setState fns for every mounted instance of this flag. */
  listeners: Set<(v: boolean) => void>;
  /** Removes the single shared keydown/keyup listeners; null when not installed. */
  shortcutCleanup: (() => void) | null;
  /** How many mounted instances requested the shortcut (ref-counted install). */
  shortcutRefs: number;
  /** Latest live instance's toast fn + copy, used by the one shortcut handler. */
  showSnackbar: SnackbarFn | null;
  friendlyDescription: string;
}

const registry = new Map<string, FlagEntry>();

const readInitial = (storageKey: string): boolean => {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const getEntry = (storageKey: string): FlagEntry => {
  let entry = registry.get(storageKey);
  if (!entry) {
    entry = {
      value: readInitial(storageKey),
      listeners: new Set(),
      shortcutCleanup: null,
      shortcutRefs: 0,
      showSnackbar: null,
      friendlyDescription: '',
    };
    registry.set(storageKey, entry);
  }
  return entry;
};

const setFlagValue = (storageKey: string, enabled: boolean): void => {
  const entry = getEntry(storageKey);
  try {
    localStorage.setItem(storageKey, String(enabled));
  } catch (e) {
    console.error('Error accessing localStorage:', e);
    return;
  }
  entry.value = enabled;
  entry.listeners.forEach((listener) => listener(enabled));
};

// Install the shared keydown/keyup listeners once per flag (ref-counted). All
// instances share this one handler, so the shortcut toggles + toasts exactly
// once regardless of how many components use the flag.
const installShortcut = (storageKey: string, shortcutKeys: string[]): void => {
  const entry = getEntry(storageKey);
  entry.shortcutRefs += 1;
  if (entry.shortcutCleanup) return;

  const pressed = new Set<string>();
  const onKeyDown = (event: KeyboardEvent) => {
    if (!event.key) return;
    pressed.add(event.key.toLowerCase());
    const allPressed = shortcutKeys.every((key) => pressed.has(key.toLowerCase()));
    if (allPressed && pressed.size === shortcutKeys.length) {
      event.preventDefault();
      event.stopPropagation();
      const next = !entry.value;
      setFlagValue(storageKey, next);
      entry.showSnackbar?.(
        `"${entry.friendlyDescription}" is now ${next ? 'enabled' : 'disabled'}.`,
        next,
      );
    }
  };
  const onKeyUp = (event: KeyboardEvent) => {
    if (!event.key) return;
    pressed.delete(event.key.toLowerCase());
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  entry.shortcutCleanup = () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };
};

const uninstallShortcut = (storageKey: string): void => {
  const entry = registry.get(storageKey);
  if (!entry) return;
  entry.shortcutRefs -= 1;
  if (entry.shortcutRefs <= 0) {
    entry.shortcutCleanup?.();
    entry.shortcutCleanup = null;
    entry.shortcutRefs = 0;
  }
};

/**
 * Hook to manage feature flags via localStorage with keyboard shortcut activation.
 * Multi-instance-safe: several components can use the same flag and they all stay
 * in sync; the shortcut toggles once and shows a single snackbar.
 *
 * @param featureName The name of the feature flag
 * @param friendlyDescription Description shown to the user when toggled
 * @param shortcutKeys Keys that must be pressed together to toggle the feature
 * @returns [isEnabled, setEnabled] - current state and a manual toggle
 */
export const useFeatureFlag = (
  featureName: string,
  friendlyDescription: string,
  shortcutKeys: string[] = [],
): [boolean, (enabled: boolean) => void] => {
  const { openSnackbar } = useSnackbar();
  const storageKey = `${LOCAL_STORAGE_PREFIX}${featureName}`;
  const [isEnabled, setIsEnabled] = useState<boolean>(() => getEntry(storageKey).value);

  // Point the shared entry's toast at a live instance, so the single shortcut
  // handler shows exactly one snackbar with the right copy.
  useEffect(() => {
    const entry = getEntry(storageKey);
    entry.friendlyDescription = friendlyDescription;
    entry.showSnackbar = (message, enabled) =>
      openSnackbar({ message, duration: 2000, severity: enabled ? 'success' : 'warning' });
  }, [storageKey, friendlyDescription, openSnackbar]);

  // Subscribe to shared value changes and sync once on mount.
  useEffect(() => {
    const entry = getEntry(storageKey);
    entry.listeners.add(setIsEnabled);
    setIsEnabled(entry.value);
    return () => {
      entry.listeners.delete(setIsEnabled);
    };
  }, [storageKey]);

  // Install the shortcut once across all instances (ref-counted). Depend on the
  // joined key signature so a fresh `shortcutKeys` array each render doesn't
  // churn the listener.
  const shortcutSignature = shortcutKeys.join('+');
  useEffect(() => {
    if (!shortcutKeys.length) return;
    installShortcut(storageKey, shortcutKeys);
    return () => uninstallShortcut(storageKey);
  }, [storageKey, shortcutSignature]);

  const setEnabled = useCallback(
    (enabled: boolean) => setFlagValue(storageKey, enabled),
    [storageKey],
  );

  return [isEnabled, setEnabled];
};
